import { useState, useEffect, useMemo } from 'react';
import { useGameStore } from '../store';
import { LABOR_DAILY_MAX } from '../constants';
import type { LaborTree } from '../types';
import { playChop } from '../utils/audio';

// ── 랜덤 비겹침 좌표 생성 ──────────────────────────────────────────────
interface Pos { x: number; y: number }

function genPositions(n: number): Pos[] {
  const MIN_DIST = 19;
  const placed: Pos[] = [];
  for (let i = 0; i < n; i++) {
    let found: Pos | null = null;
    for (let t = 0; t < 150; t++) {
      const x = 9 + Math.random() * 82;
      const y = 8 + Math.random() * 80;
      const ok = placed.every(p => {
        const dx = p.x - x, dy = p.y - y;
        return Math.sqrt(dx * dx + dy * dy) >= MIN_DIST;
      });
      if (ok) { found = { x, y }; break; }
    }
    placed.push(found ?? { x: 9 + Math.random() * 82, y: 8 + Math.random() * 80 });
  }
  return placed;
}

// ── Tree ──────────────────────────────────────────────────────────────
interface TreeProps {
  tree: LaborTree;
  idx: number;
  pos: Pos;
  onHit: (i: number) => void;
}

function Tree({ tree, idx, pos, onHit }: TreeProps) {
  const [shake, setShake] = useState(false);

  const handleClick = () => {
    if (tree.fallen) return;
    playChop();
    setShake(true);
    onHit(idx);
  };

  useEffect(() => {
    if (!shake) return;
    const t = setTimeout(() => setShake(false), 280);
    return () => clearTimeout(t);
  }, [shake]);

  const cls = [
    'tree',
    tree.size,
    tree.isGold ? 'gold' : '',
    tree.fallen ? 'fallen' : '',
    shake && !tree.fallen ? 'shake' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={cls}
      style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
      onClick={handleClick}
    >
      <span className="icon">{tree.isGold ? '🌟' : '🌲'}</span>
      {!tree.fallen && (
        <span className="pct">{Math.round(tree.prob * 100)}%</span>
      )}
    </div>
  );
}

// ── LaborScreen ─────────────────────────────────────────────────────────
export default function LaborScreen() {
  const show         = useGameStore((s) => s.showLaborScreen);
  const trees        = useGameStore((s) => s.laborTrees);
  const laborUsed    = useGameStore((s) => s.laborUsed);
  const laborMax     = useGameStore((s) => s.laborMax);
  const startLabor   = useGameStore((s) => s.startLaborSession);
  const hitTree      = useGameStore((s) => s.hitTree);
  const closeLabor   = useGameStore((s) => s.closeLabor);
  const watchLaborAd = useGameStore((s) => s.watchLaborAd);
  const setShow      = useGameStore((s) => s.setShow);

  const [confirmExit, setConfirmExit] = useState(false);

  const positions = useMemo(
    () => (trees.length > 0 ? genPositions(trees.length) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trees.length],
  );

  if (!show) return null;

  const sessionActive = trees.length > 0;
  const exhausted     = laborUsed >= laborMax;
  const canExtendAd   = laborMax < LABOR_DAILY_MAX;
  const remainTrees   = trees.filter(t => !t.fallen).length;

  // 좌측상단 ← / 하단 노역 중단 모두 호출하는 통합 exit
  const tryExit = () => {
    if (sessionActive && remainTrees > 0) {
      setConfirmExit(true);
    } else {
      closeLabor();
    }
  };

  const forceExit = () => {
    setConfirmExit(false);
    setShow('showLaborScreen', false);
  };

  return (
    <>
      <div id="labor-screen">
        {/* 헤더 — 좌측상단 ← 나가기 */}
        <div className="labor-header">
          <button className="rank-back" onClick={tryExit}>←</button>
          <div className="rank-title">⛏️ 노역하기</div>
          <div className="labor-uses">남은 노역: {Math.max(laborMax - laborUsed, 0)}회</div>
        </div>

        {/* 안내 */}
        <div className="labor-info">
          터치로 나무를 쓰러뜨리시오.<br />
          <span style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>
            클릭당 1% 확률, 누적 ↑ · 일반 100만~1,000만 · 황금 1억~10억
          </span>
        </div>

        {/* 본문 — 숲 or 빈 영역 */}
        <div id="labor-grid">
          {sessionActive && trees.map((t, i) => (
            <Tree
              key={i}
              tree={t}
              idx={i}
              pos={positions[i] ?? { x: 50, y: 50 }}
              onHit={hitTree}
            />
          ))}
        </div>

        {/* 하단 — 노역 중단 / 시작 + 광고 횟수 추가 */}
        <div className="labor-footer">
          <div style={{ display: 'flex', gap: 10 }}>
            {sessionActive ? (
              <button
                className="modal-btn ghost"
                style={{ flex: 1, marginTop: 0 }}
                onClick={tryExit}
              >
                노역 중단
              </button>
            ) : (
              <button
                className="modal-btn"
                style={{ flex: 1, marginTop: 0 }}
                disabled={exhausted}
                onClick={startLabor}
              >
                {exhausted ? '오늘 노역 끝' : '노역 시작'}
              </button>
            )}
            <button
              className="modal-btn ghost"
              style={{ flex: 1, marginTop: 0, opacity: canExtendAd ? 1 : 0.4 }}
              disabled={!canExtendAd}
              onClick={watchLaborAd}
            >
              {canExtendAd ? '+1회 추가하기' : `최대 ${LABOR_DAILY_MAX}회`}
            </button>
          </div>
        </div>
      </div>

      {/* 나가기 확인 오버레이 */}
      {confirmExit && (
        <div
          className="modal-backdrop center"
          style={{ display: 'flex', zIndex: 250 }}
          onClick={(e) => e.target === e.currentTarget && setConfirmExit(false)}
        >
          <div className="modal-sheet mid">
            <div className="modal-title">⚠️ 잠깐!</div>
            <div className="modal-body" style={{ textAlign: 'center' }}>
              아직 나무가 남아있습니다.<br />
              정말 나가시겠습니까?
            </div>
            <button className="modal-btn" onClick={forceExit}>예, 나가겠소</button>
            <button className="modal-btn ghost" onClick={() => setConfirmExit(false)}>
              계속 두드리겠소
            </button>
          </div>
        </div>
      )}
    </>
  );
}
